import { useGetAllActions } from "src/api/sure-api/actions/useGetAllActions";
import { ActionData, ActionsCsr } from "./context";
import { ScoreState } from "../EntityBasedScore/useEntityBasedScoreParams";
import { OneActionPlan } from "src/api/client-api/ActionPlan/ActionPlan/useGetOneActionPlan";
import { ScoreDetails, ScoreService } from "../EntityBasedScore/ScoreService";
import { useActionData } from "./useActionData";
import { ActionPlanData, APScore } from "./useActionPlanData";
import {
    ActionPlanAction,
    PropertiesTree,
} from "src/api/client-api/ActionPlan/ActionPlanAction/useGetOneActionPlanAction";
import { ScoreDimensionValue } from "src/api/client-api/post/scoreSchema";
import { ActionPropertyBuilder } from "../Property/ActionProperty";

export type ActionScores = {
    start: ActionScoreByCsr;
    end: ActionScoreByCsr;
};

export type ActionScoreByCsr = Partial<Record<string, ActionsCsr>>;

export type PostScoresToCompare = {
    start: {
        scores: Partial<Record<string, ScoreDimensionValue>>;
        post: APScore | undefined;
    };
    target: {
        scores: Partial<Record<string, ScoreDimensionValue>>;
        post: APScore | undefined;
    };
    posts: APScore[];
};

type Options = {
    ssqId: number;
    actionPlan: OneActionPlan | undefined;
    scoreState: ScoreState;
    scoreList: ScoreDetails;
};

export const useActionScore = (options: Options) => {
    const { ssqId, scoreState, actionPlan, scoreList } = options;

    // Request Sure Side.
    // Possibly done tomorrow by Backend;
    const { actions, getActionsByCsrTheme, isLoading } = useGetAllActions({ ssqId });
    const actionsByCsrTheme = getActionsByCsrTheme();

    const { getPostsForAction } = useActionData({ actionPlan });

    const selectPostsByCampaigns = (
        plan: ActionPlanData | undefined,
        startingCampaign: number | undefined,
        targetCampaign: number | undefined,
        includeStart?: boolean,
    ): PostScoresToCompare => {
        const postsForAction = getPostsForAction(plan, startingCampaign, targetCampaign, includeStart);

        const scoreIdForProperty = scoreState.lastSelectedVariation?.scoreId;
        const postsScoreStart = ScoreService.getScoreProperties(
            scoreIdForProperty,
            postsForAction.foundStartPost?.score2,
        );
        const postsScoreTarget = ScoreService.getScoreProperties(
            scoreIdForProperty,
            postsForAction.foundEndPost?.score2,
        );
        return {
            start: {
                scores: postsScoreStart,
                post: postsForAction.foundStartPost,
            },
            target: {
                scores: postsScoreTarget,
                post: postsForAction.foundEndPost,
            },
            posts: postsForAction.allPosts,
        };
    };

    const selectPostsByActionPlanAction = (plan: ActionPlanData, action: ActionPlanAction): PostScoresToCompare => {
        const startingCampaign = action.startSimulationSsqPost.campaign?.id;
        const targetCampaign = action.endSimulationSsqPost.campaign?.id;
        const postsByCampaign = selectPostsByCampaigns(plan, startingCampaign, targetCampaign);
        return postsByCampaign;
    };

    const getStartAndEndPropertiesBuilder = (postsScores: PostScoresToCompare, propertiesTree?: PropertiesTree) => {
        const { start, target } = postsScores;

        if (start.post?.id === undefined || target.post?.id === undefined) {
            return;
        }

        const actionDirectorStart = new ActionPropertyBuilder({
            actions: actions ?? [],
            propertiesScore: start.scores,
            postElementAndCampaign: {
                id: start.post?.id ?? -1,
                campaign: start.post?.campaign,
                elements: start.post?.elements,
            },
            precision: scoreList.lastSelected.precision,
            propertiesTree,
        });

        const actionDirectorEnd = new ActionPropertyBuilder({
            actions: actions ?? [],
            propertiesScore: target.scores,
            postElementAndCampaign: {
                id: target.post?.id ?? -1,
                campaign: target.post?.campaign,
                elements: target.post?.elements,
            },
            precision: scoreList.lastSelected.precision,
            propertiesTree,
        });

        return { actionDirectorStart, actionDirectorEnd };
    };

    const getActionScoresByCsrTheme = (
        postScores: PostScoresToCompare,
        whitelistActionIds?: number[],
        propertiesTree?: PropertiesTree,
    ): ActionScores | undefined => {
        const directors = getStartAndEndPropertiesBuilder(postScores, propertiesTree);
        if (directors === undefined) {
            return;
        }
        const startOnly = directors.actionDirectorStart.getPropertiesByCsrThemeAndActions({ whitelistActionIds });
        const endOnly = directors.actionDirectorEnd.getPropertiesByCsrThemeAndActions({ whitelistActionIds });
        return {
            start: startOnly,
            end: endOnly,
        };
    };

    const getPropertiesByAction = (
        postScores: PostScoresToCompare,
        actionId: number,
        propertiesTree?: PropertiesTree,
    ): { start: ActionData; end: ActionData } | undefined => {
        const directors = getStartAndEndPropertiesBuilder(postScores, propertiesTree);
        if (directors === undefined) {
            return undefined;
        }
        const startOnly = directors.actionDirectorStart.getPropertiesByActionId(actionId);
        const endOnly = directors.actionDirectorEnd.getPropertiesByActionId(actionId);
        return {
            start: startOnly,
            end: endOnly,
        };
    };

    return {
        isLoading,
        actions,
        actionsByCsrTheme,
        selectPostsByActionPlanAction,
        selectPostsByCampaigns,
        getActionScoresByCsrTheme,
        getPropertiesByAction,
    };
};
